import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AgentRole, ModelTier } from '@chatsdet/types';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';

/** Sentinel id for the "no override / use default" choice in provider+model selects. */
export const CATALOG_DEFAULT_ID = '';

export type AiProviderOption = { id: string; label: string };

export type AiModelCatalog = {
  providers: AiProviderOption[];
  /** provider id → model ids that provider can serve */
  models: Record<string, string[]>;
  /** provider id → tier → model used when that tier has no explicit model */
  defaultModels: Record<string, Record<ModelTier, string>>;
  /** Core agent roles and the model tier used when each role inherits. */
  agentRoles: Array<{ id: AgentRole; label: string; defaultTier: ModelTier }>;
};

/**
 * Provider → models catalog from the server (the single source of truth in
 * `ai/provider.ts`). Backs the provider/model pickers on the benchmark config
 * and run-suite override surfaces so the model options stay in lock-step with
 * what each provider can actually serve.
 */
export function useAiModelCatalogQuery() {
  return useQuery({
    queryKey: ['ai-model-catalog'],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<AiModelCatalog> => {
      const res = await fetch(`${API_URL}/superadmin/ai/model-catalog`, {
        credentials: 'include',
      });
      if (!res.ok) {
        throw await toApiError(res, 'Failed to fetch AI model catalog');
      }
      const data = (await res.json()) as {
        providers: AiProviderOption[];
        models: Record<string, string[]>;
        defaultModels: Record<string, Record<ModelTier, string>>;
        agentRoles: Array<{ id: AgentRole; label: string; defaultTier: ModelTier }>;
      };
      return {
        providers: data.providers,
        models: data.models,
        defaultModels: data.defaultModels,
        agentRoles: data.agentRoles,
      };
    },
  });
}

/**
 * Model options for a chosen provider. When no concrete provider is selected
 * (the "Default" option, i.e. an empty/null provider), returns the
 * de-duplicated union across all providers so a model can still be pinned and
 * let runtime inference route it.
 */
export function modelOptionsForProvider(
  catalog: AiModelCatalog | undefined,
  provider: string | null | undefined
): string[] {
  if (!catalog) return [];
  if (provider && catalog.models[provider]) return catalog.models[provider];
  const union = new Set<string>();
  for (const list of Object.values(catalog.models)) {
    for (const m of list) union.add(m);
  }
  return [...union];
}

/**
 * Build the provider + model `Select` option lists for a provider/model picker,
 * each prefixed with a "default" sentinel option. Shared by the benchmark
 * config form and the run-suite override dialog so both stay catalog-driven
 * with identical filtering behaviour (model list scoped to the chosen provider).
 */
export function useProviderModelOptions(
  provider: string,
  labels: { providerDefault: string; modelDefault: string }
) {
  const catalogQuery = useAiModelCatalogQuery();
  const catalog = catalogQuery.data;
  const { providerDefault, modelDefault } = labels;

  const providerOptions = useMemo(
    () => [{ id: CATALOG_DEFAULT_ID, label: providerDefault }, ...(catalog?.providers ?? [])],
    [catalog, providerDefault]
  );

  const modelOptions = useMemo(
    () => [
      { id: CATALOG_DEFAULT_ID, label: modelDefault },
      ...modelOptionsForProvider(catalog, provider || null).map((m) => ({ id: m, label: m })),
    ],
    [catalog, provider, modelDefault]
  );

  return { catalog, catalogQuery, providerOptions, modelOptions };
}
